import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactQuill, { Quill } from "react-quill";
import shortid from "shortid";

import { getFileByUrl } from "utils";
import WritingAssistantEditorToolbar from "../writing-assistant/WritingAssistantEditor/WritingAssistantEditorToolbar";
import { EDITOR_WIDTH } from "../writing-assistant/writingAssistantHelpers";
import { imagesURL } from "config";
import "../writing-assistant/WritingAssistantEditor/WritingAssistantEditor.scss"
import "./MathRodsWritingAssistantEditor.scss";
import { Clipart } from "components/flink-components";
import { WritingFeedbackButton } from "components/flink-play";
import { Portal } from "components/common";
import {splitImageDocument, joinImageDocument} from "./split-image-document.mjs";

const templateGraphicsFolder = `${imagesURL}/TemplateGraphics/WritingAssistant/`;

const formats = [
  "align",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "width",
  "list",
  "bullet",
  "indent",
  "color",
  "background",
  "style",
  "imageResize",
  "image"
];

const availableFonts = [
  "Arial",
  "Arial Black",
  "Bookman",
  "Comic Sans MS",
  "Courier",
  "Courier New",
  "Garamond",
  "Georgia",
  "Helvetica",
  "Impact",
  "Palatino",
  "Roboto",
  "Times New Roman",
  "Times",
  "Tahoma",
  "Verdana"
];

const generateBlockClass = Prototype => {
  class CustomBlock extends Prototype {
    constructor(domNode, value) {
      super(domNode, value);
      this.format("size", "18px");
    }

    static tagName = "P";

    format(name, value) {
      if (name === "size") {
        this.domNode.style.fontSize = value;
      } else {
        super.format(name, value);
      }
    }
  }

  Quill.register(CustomBlock, true);
};

class WritingAssistantEditor extends Component {
  lastSelection = null;

  constructor(props) {
    super(props);

    Quill.register(Quill.import("attributors/style/background"), true);
    Quill.register(Quill.import("attributors/style/color"), true);
    Quill.register(Quill.import("attributors/style/align"), true);

    const fonts = Quill.import("attributors/style/font");
    fonts.whitelist = availableFonts;
    Quill.register(fonts, true);

    const sizes = Quill.import("attributors/style/size");
    delete sizes.whitelist;
    Quill.register(sizes, true);

    this.Block = Quill.import("blots/block");
    generateBlockClass(this.Block);

    this.ImageFormat = Quill.import("formats/image");

    // Custom icon for import art
    const icons = Quill.import("ui/icons");
    delete icons.image; // unset default image icon

    class CustomImageFormat extends this.ImageFormat {
      static create(value) {
        const node = super.create(value);
        node.style.display = "block";
        node.style.margin = "0 auto";
        node.style.maxWidth = "100%";
        return node;
      }
    }
    Quill.register(CustomImageFormat, true);

    const toolbarID = shortid.generate();

    const formatsToUse = [...formats];

    const modules = {
      clipboard: {
        matchers: [["image", null]],
        matchVisual: false
      },
      toolbar: {
        container: `[data-toolbar-id="${toolbarID}"]`
      }
      // imageResize: {
      //   parchment: Quill.import("parchment")
      // }
    };

    this.state = {
      editorHtml: "",
      toolbarID,
      formats: formatsToUse,
      modules,
      showClipart: false,
      workspaceImage: null
    };
  }

  componentDidMount() {
    const { initialValue } = this.props;

    if (initialValue) {
      const document=this.props.separateWorkspaceImage ? splitImageDocument(initialValue) : {text:initialValue,image:null};
      this.setState({editorHtml:document.text,workspaceImage:document.image});
    }
  }

  componentWillUnmount() {
    Quill.register(this.ImageFormat, true);
  }

  editorRef = React.createRef();

  getEditor = () => {
    const editor = this.editorRef.current && this.editorRef.current.getEditor();
    return editor;
  };

  getText = () => {
    const editor = this.getEditor();
    return editor.getText();
  };

  rememberSelection = range => {
    if (range) {
      this.lastSelection = { index: range.index, length: range.length || 0 };
    }
  };

  getInsertionRange = editor => {
    const { preventProgrammaticFocus = false } = this.props;
    const currentRange = preventProgrammaticFocus
      ? editor.getSelection()
      : editor.getSelection(true);

    if (currentRange) {
      this.rememberSelection(currentRange);
      return currentRange;
    }

    const maxIndex = Math.max(0, editor.getLength() - 1);
    const fallback = this.lastSelection || { index: maxIndex, length: 0 };
    return {
      index: Math.min(Math.max(0, fallback.index || 0), maxIndex),
      length: Math.max(0, fallback.length || 0),
    };
  };

  rememberProgrammaticCursor = (editor, index) => {
    const { preventProgrammaticFocus = false } = this.props;
    this.lastSelection = { index, length: 0 };

    if (!preventProgrammaticFocus) {
      editor.setSelection(index, 0, Quill.sources.SILENT);
    }
  };

  loadHtml = html => {
    this.lastSelection = null;
    this.setState({ editorHtml: html || '' });
  };

  pasteHtml = html => {
    const { saveChanges } = this.props;
    const { preventProgrammaticFocus = false } = this.props;
    const editor = this.getEditor();
    if (!editor) return;

    const range = this.getInsertionRange(editor);

    if (preventProgrammaticFocus) {
      const Delta = Quill.import('delta');
      const converted = editor.clipboard.convert(html || '');
      const insertDelta = new Delta()
        .retain(range.index)
        .delete(range.length)
        .concat(converted);

      editor.updateContents(insertDelta, Quill.sources.USER);
      this.rememberProgrammaticCursor(editor, range.index + converted.length());
    } else {
      const beforeLength = editor.getLength();
      editor.clipboard.dangerouslyPasteHTML(range.index, html);
      const insertedLength = Math.max(0, editor.getLength() - beforeLength);
      this.rememberProgrammaticCursor(editor, range.index + insertedLength);
    }

    saveChanges && setTimeout(saveChanges, 1);
  };

  insertImageByUrl = url => {
    getFileByUrl(url).then(file => {
      this.insertImage(file);
    });
  };

  insertImage = file => {
    const { saveChanges } = this.props;
    const editor = this.getEditor();
    if (!editor) return;
    const range = this.getInsertionRange(editor);

    const Delta = Quill.import("delta");

    const reader = new FileReader();
    reader.onload = e => {
      editor.updateContents(
        new Delta()
          .retain(range.index)
          .delete(range.length)
          .insert({ image: e.target.result }),
        Quill.sources.USER
      );

      this.rememberProgrammaticCursor(editor, range.index + 1);

      saveChanges && setTimeout(saveChanges, 1);
    };

    reader.readAsDataURL(file);
  };

  insertWord = word => {
    const editor = this.getEditor();
    if (!editor) return;

    const range = this.getInsertionRange(editor);
    editor.insertText(range.index, " " + word, Quill.sources.USER);
    this.rememberProgrammaticCursor(editor, range.index + word.length + 1);
  };

  handleChange = html => {
    const { editorHtml } = this.state;
    if (editorHtml === html) return;

    this.setState({ editorHtml: html });
    this.props.changeHandler(this.props.separateWorkspaceImage ? joinImageDocument(html,this.state.workspaceImage) : html);
  };

  removeWorkspaceImage = () => {
    this.setState({workspaceImage:null},()=>this.props.changeHandler(joinImageDocument(this.state.editorHtml,null)));
  };

  toggleClipart = state => {
    this.setState({ showClipart: state });
  };

  getWritingAssistantContent = () => {
    const editor = this.editorRef.current.getEditor();
    return editor.getText();
  }

  render() {
    const { editorHtml, toolbarID, modules, formats, showClipart, workspaceImage } = this.state;
    const {
      words,
      images,
      reading,
      writingFeedbackEnabled = true,
      separateWorkspaceImage = false,
    } = this.props;

    const clipartImg = templateGraphicsFolder + "clipArtLibrary.png";
    const importArtImg = templateGraphicsFolder + "importArt.png";

    const editorStyle = {
      width: EDITOR_WIDTH + "px"
    };

    const hasWords = !!(words && words.length);
    const hasImages = !!(images && images.length);

    return (
      <div className="writing-assistant-editor">
        <WritingAssistantEditorToolbar
          id={toolbarID}
          clipartButton={
            <button onClick={() => this.toggleClipart(true)}>
              <img src={clipartImg} alt="" />
            </button>
          }
          importArtButton={
            <button className="ql-image">
              <img src={importArtImg} alt="" />
            </button>
          }
          writingFeedbackButton={writingFeedbackEnabled ? (
            <WritingFeedbackButton getContent={this.getWritingAssistantContent} />
          ) : null}
        />

        {hasWords && (
          <div className="wordsWrapper">
            {words.map((w, idx) => (
              <span key={idx} onClick={() => this.insertWord(w)}>
                {w}
              </span>
            ))}
          </div>
        )}

        {hasImages && (
          <div className="imagesWrapper">
            {images.map((src, idx) => (
              <span
                style={{ backgroundImage: `url("${src}")` }}
                key={idx}
                onClick={() => this.insertImageByUrl(src)}
              >
                {/* <img src={src} alt="" /> */}
              </span>
            ))}
          </div>
        )}

        {reading && (
          <ReactQuill
            style={editorStyle}
            theme="snow"
            value={reading}
            readOnly
            modules={{ toolbar: null }}
            formats={formats}
            className={`reading-area ${hasImages ? "small" : ""}`}
            preserveWhitespace
          />
        )}

        <div className={separateWorkspaceImage ? "writing-assistant-document-split" : "writing-assistant-document"}>
        {separateWorkspaceImage && <div className="workspace-image-pane">
          <div className="workspace-image-viewport" role="region" aria-label="Copied rod arrangement" tabIndex={0}>
            {workspaceImage && <img src={workspaceImage.src} alt="Math Rods workspace" />}
          </div>
        </div>}
        <ReactQuill
          ref={this.editorRef}
          style={editorStyle}
          theme="snow"
          onChange={this.handleChange}
          onChangeSelection={this.rememberSelection}
          value={editorHtml}
          modules={modules}
          formats={formats}
          className="editor"
          preserveWhitespace
        />
        </div>

        <Portal>
          <Clipart
            returnFile
            show={showClipart}
            onClose={() => this.toggleClipart(false)}
            onPick={file => {
              this.toggleClipart(false);
              this.insertImage(file);
            }}
          />
        </Portal>
      </div>
    );
  }
}

WritingAssistantEditor.defaultProps = {
  preventProgrammaticFocus: false,
};

WritingAssistantEditor.propTypes = {
  preventProgrammaticFocus: PropTypes.bool,
  writingFeedbackEnabled: PropTypes.bool,
  separateWorkspaceImage: PropTypes.bool,
  reading: PropTypes.string,
  images: PropTypes.array,
  words: PropTypes.array,
  initialValue: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default WritingAssistantEditor;
